import { Link } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { useLearningSessionStatus } from '@/providers/learning-session-context';
import { useLearningSessionTrackerEnabled } from '@/hooks/useLearningSessionTracker';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDuration } from '@/lib/format-duration';

function progressPercent(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((used / total) * 100));
}

export function LearningSessionStatusCard() {
  const enabled = useLearningSessionTrackerEnabled();
  const status = useLearningSessionStatus();
  const { hasPermission } = usePermissions();
  const canManageSettings = hasPermission(PERMISSIONS.PROGRESO_ADMINISTRAR);

  if (!enabled || !status?.config) {
    return null;
  }

  const { config, currentSessionSeconds, todaySeconds } = status;
  const sessionTotal = config.maxSessionSeconds;
  const dailyTotal = config.maxDailySeconds;
  const sessionUsed = Math.min(currentSessionSeconds, sessionTotal);
  const dailyUsed = Math.min(todaySeconds, dailyTotal);

  return (
    <aside className="lms-learning-session-status" aria-label="Tiempo de sesión">
      <div className="lms-learning-session-status__header">
        <span className="material-symbols-outlined lms-learning-session-status__icon" aria-hidden="true">
          timer
        </span>
        <div>
          <p className="lms-learning-session-status__title">Tiempo de sesión</p>
          <p className="lms-learning-session-status__hint">Se actualiza mientras navegas</p>
        </div>
      </div>

      <div className="lms-learning-session-status__metrics">
        <div className="lms-learning-session-status__metric">
          <span className="lms-learning-session-status__label">Sesión actual</span>
          <strong className="lms-learning-session-status__value">{formatDuration(sessionUsed)}</strong>
          <div className="lms-learning-session-status__bar" role="presentation">
            <span
              className="lms-learning-session-status__bar-fill"
              style={{ width: `${progressPercent(sessionUsed, sessionTotal)}%` }}
            />
          </div>
          <span className="lms-learning-session-status__limit">
            de {formatDuration(sessionTotal)}
          </span>
        </div>

        <div className="lms-learning-session-status__metric">
          <span className="lms-learning-session-status__label">Hoy</span>
          <strong className="lms-learning-session-status__value">{formatDuration(dailyUsed)}</strong>
          <div className="lms-learning-session-status__bar" role="presentation">
            <span
              className="lms-learning-session-status__bar-fill lms-learning-session-status__bar-fill--today"
              style={{ width: `${progressPercent(dailyUsed, dailyTotal)}%` }}
            />
          </div>
          <span className="lms-learning-session-status__limit">
            de {formatDuration(dailyTotal)}
          </span>
        </div>
      </div>

      {canManageSettings ? (
        <Link to="/progreso/configuracion-sesion" className="lms-learning-session-status__link">
          Ver límites
          <span className="material-symbols-outlined" aria-hidden="true">
            chevron_right
          </span>
        </Link>
      ) : null}
    </aside>
  );
}
